import {
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { entryKeyOf, navigationOf, SaphirNavigationItem } from '@saphir/module-contract';

import { MODULE_ROUTE_PREFIX } from '../../core/modules/module-routes';
import { LicenseContextService } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { CommandPaletteService } from './command-palette.service';

/** One destination the palette can take the user to. */
interface PaletteEntry {
  /** Unique per destination — the joined link is exactly that. */
  readonly key: string;
  readonly labelKey: string;
  /** The module the entry belongs to; absent on top-level destinations. */
  readonly detailKey?: string;
  readonly icon: string;
  readonly link: string[];
  /** Lower-cased translated text the filter runs against. */
  readonly haystack: string;
}

/**
 * Ctrl+K: every destination the shell knows, one keystroke away.
 *
 * The entries are the same data the sidebar renders — manifests and each
 * definition's `navigation`, flattened — so the palette needs nothing from a
 * module beyond what the contract already carries. The filter matches the
 * *translated* labels plus the module id, in whichever language is active.
 *
 * `position: fixed` is fine here and forbidden in modules for the same reason:
 * the overlay belongs to whoever owns the window, and the shell does.
 */
@Component({
  selector: 'app-command-palette',
  imports: [TranslatePipe],
  templateUrl: './command-palette.html',
  styleUrl: './command-palette.scss',
  host: {
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class CommandPalette {
  private readonly registry = inject(ModuleRegistryService);
  private readonly licenses = inject(LicenseContextService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly palette = inject(CommandPaletteService);

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('search');

  protected readonly isOpen = this.palette.isOpen;
  protected readonly query = signal('');

  private readonly entries = computed<PaletteEntry[]>(() => {
    // Tracked so the haystacks re-translate when the language resolves.
    this.translate.currentLang();

    const entries: PaletteEntry[] = [
      this.entry('sidebar.dashboard', 'pi pi-home', ['/', MODULE_ROUTE_PREFIX]),
    ];

    for (const module of this.registry.availableModules()) {
      // Same rule as the sidebar: what the licence does not cover — or what
      // UMC has not ruled on yet — the palette does not offer.
      if (!this.licenses.settled() || this.licenses.isBlocked(module.id)) {
        continue;
      }

      const titleKey = module.manifest?.titleKey ?? module.id;
      const icon = module.manifest?.icon ?? 'pi pi-box';
      const base = ['/', MODULE_ROUTE_PREFIX, module.id];

      entries.push(this.entry(titleKey, icon, base, undefined, module.id));
      this.collect(navigationOf(this.registry.definitionOf(module.id)), {
        base,
        icon,
        titleKey,
        moduleId: module.id,
        into: entries,
      });
    }

    return entries;
  });

  protected readonly filtered = computed(() => {
    const term = this.query().trim().toLowerCase();

    return term ? this.entries().filter((entry) => entry.haystack.includes(term)) : this.entries();
  });

  /** Reset to the top whenever the result list changes. */
  protected readonly selected = linkedSignal({
    source: this.filtered,
    computation: () => 0,
  });

  constructor() {
    // Focus when opened, and scroll the selection into view as it moves. After
    // render, because both need the elements the signals just decided to show.
    afterRenderEffect(() => {
      if (this.isOpen()) {
        this.searchInput()?.nativeElement.focus();
      }
    });
    afterRenderEffect(() => {
      this.selected();
      document.querySelector('.palette__entry--selected')?.scrollIntoView({ block: 'nearest' });
    });
  }

  protected onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.palette.toggle();
    }
  }

  protected onSearchKeydown(event: KeyboardEvent): void {
    const results = this.filtered();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selected.update((index) => Math.min(index + 1, results.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selected.update((index) => Math.max(index - 1, 0));
        break;
      case 'Enter': {
        const entry = results[this.selected()];

        if (entry) {
          this.activate(entry);
        }
        break;
      }
      case 'Escape':
        this.close();
        break;
    }
  }

  protected activate(entry: PaletteEntry): void {
    void this.router.navigate(entry.link);
    this.close();
  }

  protected close(): void {
    this.palette.hide();
    this.query.set('');
  }

  private collect(
    items: readonly SaphirNavigationItem[],
    context: {
      base: string[];
      icon: string;
      titleKey: string;
      moduleId: string;
      into: PaletteEntry[];
    },
  ): void {
    for (const item of items) {
      if (item.route !== undefined) {
        const link = item.route ? [...context.base, ...item.route.split('/')] : context.base;

        // The module root is already listed as the module itself.
        if (item.route) {
          context.into.push(
            this.entry(
              item.labelKey,
              item.icon ?? context.icon,
              link,
              context.titleKey,
              context.moduleId,
              // Scoped by module: `entryKeyOf` is unique WITHIN one module,
              // and this list holds every module at once.
              `${context.moduleId}/${entryKeyOf(item)}`,
            ),
          );
        }
      }

      if (item.children?.length) {
        this.collect(item.children, context);
      }
    }
  }

  private entry(
    labelKey: string,
    icon: string,
    link: string[],
    detailKey?: string,
    moduleId?: string,
    key?: string,
  ): PaletteEntry {
    const haystack = [
      this.translate.instant(labelKey) as string,
      detailKey ? (this.translate.instant(detailKey) as string) : '',
      moduleId ?? '',
    ]
      .join(' ')
      .toLowerCase();

    // The joined link where the caller supplies no key — the module rows and
    // the shell's own destinations, which are not navigation entries and have
    // no id to resolve.
    return { key: key ?? link.join('/'), labelKey, detailKey, icon, link, haystack };
  }
}
